// Pure logic for the server-side Shopify install redirect (middleware.js).
// Shared by the SPA and Vercel's Edge runtime, so it must stay free of React,
// the Supabase client and anything browser-only. Only Web APIs are used
// (URL, crypto.getRandomValues, btoa, fetch, Response, AbortController).
//
// Why this exists: Shopify's automated "Immediately authenticates after
// install" check records the URL it lands on. A client-side redirect (AuthGate's
// install capture effect) loses the race against the signed-out route guard,
// which sends the visitor to /login first. Answering the install request with a
// 302 from the edge makes the grant screen the first thing the checker sees.

// This app's own one-shot query params (handled by AuthGate's effects). Never
// swept into a captured Shopify install query. AuthGate builds its strip
// helpers from the same names, so there is one list.
export const LOGOUT_PARAM = ["logout"];
export const ADVERTISER_ID_PARAM = ["advertiser_id"];
export const GA_PARAM = ["ga"];
// origin=install&connect_shop=<shop> (DEV-2332) ride along on the same
// status=pending redirect as confirm, stripped together with it.
export const INTEGRATION_RESULT_PARAMS = ["integration", "status", "reason", "confirm", "origin", "connect_shop"];

// The ?integration= values AuthGate handles: the sales sources (ids of
// SALES_SOURCES, kept equal by a test). Any other value belongs to the screen
// that started that OAuth flow (e.g. QuickBooksConnectionCard on /support/quickbooks),
// which reads and strips its own params; AuthGate leaves them alone. A copy
// rather than an import of salesSources.js because middleware.js (Vercel
// Routing Middleware) imports this module, and the registry would pull
// shopifyService, supabase and React code into the edge bundle.
export const SALES_INTEGRATION_SOURCES = ["shopify", "amazon"];
export const APP_OWN_PARAMS = [...LOGOUT_PARAM, ...ADVERTISER_ID_PARAM, ...GA_PARAM, ...INTEGRATION_RESULT_PARAMS];

export const INSTALL_NONCE_COOKIE = "cpa_ui_shopify_install_nonce";
export const INSTALL_ORIGIN = "https://console.audiohook.com";
const COOKIE_MAX_AGE_SECONDS = 60 * 60; // same 1 hour as the localStorage entry's TTL
const DEFAULT_TIMEOUT_MS = 5000;

/**
 * Returns the install query string to forward to begin-install, or null when
 * the URL is not a Shopify install request: path must be `/`, `shop` and
 * `hmac` must both be present, and neither `code` nor `state` may be (that is
 * an OAuth callback, never an install). Every param except this app's own
 * one-shot params is forwarded.
 */
export function extractInstallQuery(requestUrl) {
  let url;
  try {
    url = new URL(requestUrl);
  } catch {
    return null;
  }
  if (url.pathname !== "/") return null;
  const params = url.searchParams;
  if (!params.get("shop") || !params.get("hmac")) return null;
  if (params.has("code") || params.has("state")) return null;
  const captured = new URLSearchParams();
  for (const [key, value] of params.entries()) {
    if (APP_OWN_PARAMS.includes(key)) continue;
    captured.append(key, value);
  }
  return captured.toString();
}

/**
 * A random, URL-safe (base64url) nonce: 32 random bytes. Binds the eventual
 * confirm step to the browser that started the install (see the long comment
 * in shopifyService.js's original definition, DEV-2332). Works in the browser
 * and in the Edge runtime.
 */
export function generateInstallNonce() {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  let binary = "";
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/**
 * Set-Cookie value handing the nonce to the SPA. Same object shape as the
 * localStorage entry. Deliberately NOT HttpOnly: the SPA must read it.
 */
export function buildInstallNonceCookie({ nonce, shop, savedAt }) {
  const value = encodeURIComponent(JSON.stringify({ nonce, shop, savedAt }));
  return `${INSTALL_NONCE_COOKIE}=${value}; Path=/; Max-Age=${COOKIE_MAX_AGE_SECONDS}; Secure; SameSite=Lax`;
}

/**
 * Answers a Shopify install request with a 302 to Shopify's grant screen, or
 * returns null (meaning: pass through to the SPA, whose in-page capture is the
 * fallback) when the request is not an install, supabaseUrl is missing, or
 * anything at all goes wrong. Dependencies are injected for unit testing.
 */
export async function handleInstallRequest(
  request,
  { fetchImpl, supabaseUrl, anonKey, now, randomNonce = generateInstallNonce, timeoutMs = DEFAULT_TIMEOUT_MS, ResponseImpl = Response },
) {
  const installQuery = extractInstallQuery(request.url);
  if (installQuery === null || !supabaseUrl) return null;

  const controller = new AbortController();
  let timer;
  try {
    // Race the fetch against the timeout so a fetch that ignores the abort
    // signal still cannot hang the page load.
    const timeout = new Promise((_, reject) => {
      timer = setTimeout(() => {
        controller.abort();
        reject(new Error("begin-install timed out"));
      }, timeoutMs);
    });
    const nonce = randomNonce();
    // Same shop source as the SPA (AuthGate): the install query's own `shop`.
    const shop = new URL(request.url).searchParams.get("shop");
    const headers = { "Content-Type": "application/json", Origin: INSTALL_ORIGIN };
    if (anonKey) {
      headers.apikey = anonKey;
      headers.Authorization = `Bearer ${anonKey}`;
    }
    const call = (async () => {
      const response = await fetchImpl(`${supabaseUrl}/functions/v1/shopify-oauth`, {
        method: "POST",
        headers,
        body: JSON.stringify({ action: "begin-install", install_query: installQuery, install_nonce: nonce }),
        signal: controller.signal,
      });
      if (!response || !response.ok) return null;
      const body = await response.json();
      return body && typeof body.url === "string" && body.url ? body.url : null;
    })();
    call.catch(() => {}); // a late failure after the timeout must not go unhandled
    const url = await Promise.race([call, timeout]);
    if (!url) return null;
    return new ResponseImpl(null, {
      status: 302,
      headers: {
        Location: url,
        "Set-Cookie": buildInstallNonceCookie({ nonce, shop, savedAt: now }),
        "Cache-Control": "no-store",
      },
    });
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}
